import React, { useState, useEffect } from "react";
import {
  Typography,
  Skeleton,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { brandColors, designTokens } from "~/presentation/themes/modernTheme";

const TRUSTED_VIDEO_DOMAINS = [
  "youtube.com",
  "www.youtube.com",
  "youtu.be",
  "player.vimeo.com",
  "vimeo.com"
];

function isTrustedVideoUrl(rawUrl) {
  if (!rawUrl) return false;
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== "https:") return false;
    return TRUSTED_VIDEO_DOMAINS.some(
      (domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

const categorySelectSx = {
  mb: `${designTokens.spacing.md}px`,
};

const videoCardSx = {
  mb: `${designTokens.spacing.md}px`,
  borderRadius: `${designTokens.borderRadius.lg}px`,
  border: `1px solid ${brandColors.neutral[200]}`,
  backgroundColor: brandColors.neutral[50],
  overflow: "hidden",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: brandColors.primary[300],
    boxShadow: designTokens.shadows.card,
  },
};

const videoCardBodySx = {
  px: `${designTokens.spacing.md}px`,
  py: `${designTokens.spacing.sm}px`,
};

const videoTitleSx = {
  m: 0,
  mt: `${designTokens.spacing.sm}px`,
  fontWeight: designTokens.typography.fontWeights.semibold,
  color: brandColors.neutral[900],
  lineHeight: 1.4,
};

const videoEmbedSx = {
  position: "relative",
  paddingBottom: "56.25%",
  height: 0,
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
  },
};

const htmlContentSx = {
  mt: `${designTokens.spacing.sm}px`,
  color: brandColors.neutral[600],
  fontSize: "0.875rem",
  lineHeight: 1.6,
  wordBreak: "break-word",
  "& p": {
    m: 0,
    mb: 1,
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: `${designTokens.borderRadius.sm}px`,
  },
  "& table": {
    width: "100%",
    maxWidth: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${brandColors.neutral[200]}`,
    mb: 1,
  },
  "& td, & th": {
    padding: "6px 8px",
    border: `1px solid ${brandColors.neutral[200]}`,
    fontSize: "0.8125rem",
  },
  "& a": {
    color: brandColors.primary[500],
    textDecoration: "underline",
  },
  "& ul, & ol": {
    pl: 2.5,
    mb: 1,
  },
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    mt: 1,
    mb: 0.5,
    color: brandColors.neutral[800],
  },
};

const emptyStateSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  p: `${designTokens.spacing.xxl}px`,
  textAlign: "center",
  color: brandColors.neutral[500],
};

function VideoCard({ video }) {
  const hasVideo = isTrustedVideoUrl(video.videoUrl);
  const hasText = Boolean(video.text);

  return (
    <Box sx={videoCardSx}>
      {hasVideo && (
        <Box sx={videoEmbedSx}>
          <iframe
            src={video.videoUrl}
            title={video.title}
            sandbox="allow-scripts allow-same-origin allow-popups"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      )}

      <Box sx={videoCardBodySx}>
        <Typography variant="subtitle1" component="h3" sx={videoTitleSx}>
          {video.title}
        </Typography>

        {hasText && (
          <Box
            component="div"
            sx={htmlContentSx}
            dangerouslySetInnerHTML={{ __html: video.text }}
          />
        )}
      </Box>
    </Box>
  );
}

function VideoList({ videos }) {
  if (!videos || videos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nenhum conteudo disponivel nesta categoria.
      </Typography>
    );
  }

  return (
    <Box component="section" aria-label="Videos de ajuda">
      {videos.map((video) => (
        <VideoCard key={video.id} video={video} />
      ))}
    </Box>
  );
}

function LoadingSkeleton() {
  return (
    <Box>
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 1 }} />
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="30%" height={20} />
        </Box>
      ))}
    </Box>
  );
}

function EmptyContent() {
  return (
    <Box sx={emptyStateSx}>
      <HelpOutlineIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Central de Ajuda
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Nenhum conteudo de ajuda disponivel para esta pagina no momento.
      </Typography>
    </Box>
  );
}

export default function HelpCenterContent({ categories, isLoading, error }) {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setActiveTab(0);
  }, [categories]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <Box sx={emptyStateSx}>
        <Typography variant="body2" color="error">
          Ocorreu um erro ao carregar o conteudo de ajuda. Tente novamente mais tarde.
        </Typography>
      </Box>
    );
  }

  if (!categories || categories.length === 0) {
    return <EmptyContent />;
  }

  const hasMultipleCategories = categories.length > 1;

  const clampedIndex = Math.min(activeTab, categories.length - 1);
  const activeCategory = categories[clampedIndex];

  return (
    <>
      {hasMultipleCategories && (
        <Autocomplete
          options={categories}
          getOptionLabel={(category) => category.title}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={activeCategory}
          onChange={(_event, newValue) => {
            if (newValue) {
              setActiveTab(categories.indexOf(newValue));
            }
          }}
          disableClearable
          size="small"
          sx={categorySelectSx}
          renderInput={(params) => (
            <TextField {...params} label="Filtrar categoria de ajuda" placeholder="Buscar categoria..." />
          )}
        />
      )}

      <Box>
        {activeCategory.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {activeCategory.description}
          </Typography>
        )}
        <VideoList videos={activeCategory.explanatoryVideos} />
      </Box>
    </>
  );
}
